// DIAS Customization Start - Email OTP MFA for ICM local login
import { Injectable } from '@angular/core';

import { SpecialHttpErrorHandler } from 'ish-core/interceptors/icm-error-mapper.interceptor';

interface MfaChallengeRO {
  type: 'MfaChallengeRO';
  error: 'MFA_REQUIRED';
  challengeId: string;
  expiresAt: string;
  otpLength: number;
}

interface MfaErrorRO {
  type: 'MfaErrorRO';
  error:
    | 'INVALID_MFA_CODE'
    | 'MFA_CODE_EXPIRED'
    | 'MFA_CHALLENGE_LOCKED'
    | 'MFA_CHALLENGE_NOT_FOUND'
    | 'MFA_RESEND_TOO_SOON'
    | 'MFA_TOO_MANY_ATTEMPTS'
    | 'MFA_EMAIL_DELIVERY_FAILED'
    | 'MFA_SERVICE_UNAVAILABLE'
    | 'MFA_SERVICE_DISABLED';
}

// translation keys for the MFA-verify/resend error codes, following the 'account.login.<field>.<error>' convention
const MFA_ERROR_TRANSLATION_KEYS: Record<MfaErrorRO['error'], string> = {
  INVALID_MFA_CODE: 'account.login.otp.error.invalid',
  MFA_CODE_EXPIRED: 'account.login.otp.error.expired',
  MFA_CHALLENGE_LOCKED: 'account.login.otp.error.locked',
  MFA_CHALLENGE_NOT_FOUND: 'account.login.otp.error.not_found',
  MFA_RESEND_TOO_SOON: 'account.login.otp.resend.error.too_soon',
  MFA_TOO_MANY_ATTEMPTS: 'account.login.otp.resend.error.too_many_attempts',
  MFA_EMAIL_DELIVERY_FAILED: 'account.login.otp.error.email_delivery_failed',
  MFA_SERVICE_UNAVAILABLE: 'account.login.otp.error.service_unavailable',
  MFA_SERVICE_DISABLED: 'account.login.otp.error.service_disabled',
};

/**
 * Handles the ICM MFA response bodies ('MfaChallengeRO' from /token, 'MfaErrorRO' from /token and /mfa-resend).
 * Registered before {@link LoginUserErrorHandler} in {@link ConfigurationModule} - that handler otherwise matches
 * every 401/403 from the token endpoint (including the MFA ones) and would map away the challenge data.
 */
@Injectable()
export class MfaErrorHandler implements SpecialHttpErrorHandler {
  test: SpecialHttpErrorHandler['test'] = error =>
    error.error?.type === 'MfaChallengeRO' || error.error?.type === 'MfaErrorRO';

  map: SpecialHttpErrorHandler['map'] = error => {
    const body: MfaChallengeRO | MfaErrorRO = error.error;
    if (body.type === 'MfaChallengeRO') {
      return {
        code: 'MFA_REQUIRED',
        mfaChallenge: {
          challengeId: body.challengeId,
          expiresAt: body.expiresAt,
          otpLength: body.otpLength,
        },
      };
    }
    const restartRequired = [
      'MFA_SERVICE_DISABLED',
      'MFA_CODE_EXPIRED',
      'MFA_CHALLENGE_LOCKED',
      'MFA_CHALLENGE_NOT_FOUND',
      'MFA_TOO_MANY_ATTEMPTS',
    ].includes(body.error);
    return {
      code: MFA_ERROR_TRANSLATION_KEYS[body.error] ?? 'account.login.otp.error.unexpected',
      ...(restartRequired ? { mfaRestartRequired: true } : {}),
    };
  };
}
// DIAS Customization End
